'use client'

import { Award, Coins, Gift, Shirt, Star, Ticket } from 'lucide-react'

import { usePassRewardDetails } from '../hooks/usePassRewardDetails'
import { formatDateTime } from '../lib/content-history-labels'
import type { PassRewardEntry } from '../types'

import { Dialog } from '@/shared/ui/dialog'
import { Modal, ModalDescription, ModalTitle } from '@/shared/ui/Modal'

interface PassRewardDetailsModalProps {
  progressResourceId: number | null
  entryName: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const REWARD_KIND_LABELS: Record<PassRewardEntry['kind'], string> = {
  coins: 'Moedas',
  points: 'Pontos',
  reward_item: 'Item de recompensa',
  cosmetic: 'Cosmético',
  title: 'Título'
}

const REWARD_KIND_ICONS: Record<PassRewardEntry['kind'], typeof Coins> = {
  coins: Coins,
  points: Star,
  reward_item: Gift,
  cosmetic: Shirt,
  title: Award
}

// Moedas e pontos são quantidade; os demais são um alvo nomeado.
function rewardValue(reward: PassRewardEntry): string {
  if (reward.kind === 'coins' || reward.kind === 'points') {
    return String(reward.amount ?? 0)
  }
  return reward.target_name ?? '—'
}

export function PassRewardDetailsModal({
  progressResourceId,
  entryName,
  open,
  onOpenChange
}: PassRewardDetailsModalProps) {
  const { record, isLoading } = usePassRewardDetails(open ? progressResourceId : null)
  const rewards = record?.rewards ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Modal className="sm:max-w-lg">
        <ModalTitle className="flex items-center gap-2">
          <Ticket className="size-5 text-primary" aria-hidden />
          {record?.name ?? entryName ?? 'Resgate de passe'}
        </ModalTitle>
        <ModalDescription>
          {record?.created_at
            ? `Recompensas recebidas em ${formatDateTime(record.created_at)}.`
            : 'Recompensas recebidas neste resgate.'}
        </ModalDescription>

        {isLoading || !record ? (
          <p className="py-8 text-center text-sm font-semibold text-muted-foreground">
            {isLoading ? 'Carregando recompensas…' : 'Não foi possível carregar o resgate.'}
          </p>
        ) : rewards.length === 0 ? (
          <p className="mt-2 rounded-2xl border-2 border-dashed border-border p-6 text-center text-sm font-semibold italic text-muted-foreground">
            Nenhuma recompensa registrada neste resgate.
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-2">
            {rewards.map((reward, index) => {
              const Icon = REWARD_KIND_ICONS[reward.kind] ?? Gift

              return (
                <li
                  key={`${reward.kind}-${reward.target_id ?? index}`}
                  className="flex items-center gap-3 rounded-2xl border border-border bg-white/70 px-4 py-3 shadow-[0_2px_0_rgba(16,89,216,0.12)]"
                >
                  {reward.target_image_url ? (
                    <img
                      src={reward.target_image_url}
                      alt=""
                      className="size-10 shrink-0 rounded-xl object-cover"
                    />
                  ) : (
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="size-5" aria-hidden />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                      {REWARD_KIND_LABELS[reward.kind] ?? reward.kind}
                    </p>
                    <p className="truncate text-sm font-bold text-primary-dark">
                      {rewardValue(reward)}
                    </p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Modal>
    </Dialog>
  )
}
